import { formatCurrency, priceUnitLabel, roundToCents } from '~/utils';
import type { Quantity } from '~/graphql/graphql';
import { Package, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PricePerPieceTooltip } from '~/components/ui';
import { VehiclePayloadLabel } from '~/components/ui/VehiclePayloadLabel';
import type { TransportMode } from '~/hooks/useTransportState';

export function ItemCard({
  productName,
  itemQuantity,
  unit,
  pricePerUnit,
  currency = 'USD',
  length,
  gradeInformation,
  gradeLabel,
  buyCode,
  suggestedPrice,
  buyerView = false,
  vehiclePayload,
  vehicleLabel,
  piecesPerBundle,
  vehiclePiecesCount,
  totalPieces,
  pricePerPiece,
  pricePerPieceRaw,
  suggestedPricePerPiece,
  suggestedPricePerPieceRaw,
}: Readonly<{
  productName: string;
  itemQuantity: number;
  unit: string;
  pricePerUnit: number | null;
  /** Currency code for all price displays — defaults to 'USD'. */
  currency?: string;
  length: string;
  gradeInformation: string;
  gradeLabel: string;
  buyCode?: string | null;
  suggestedPrice?: number | null;
  buyerView?: boolean;
  vehiclePayload?: Quantity | null;
  vehicleLabel?: TransportMode;
  piecesPerBundle?: Quantity | null;
  /** Pieces that fit in one vehicle (shown next to capacity when unit ≠ PCS). */
  vehiclePiecesCount?: number;
  /** Total pieces in this line item (shown next to pieces-per-bundle). */
  totalPieces?: number;
  /**
   * 6dp-rounded price per piece — shown when unit ≠ PCS. Total arithmetic
   * prefers pricePerPieceRaw and only falls back to this rounded value.
   */
  pricePerPiece?: number;
  /**
   * Unrounded price per piece — used ONLY for total arithmetic; falls back to
   * the (rounded) pricePerPiece prop if not supplied, for backward
   * compatibility with any caller that doesn't pass a raw variant.
   */
  pricePerPieceRaw?: number;
  /** Price per piece based on buyer's suggested price — shown when unit ≠ PCS and a suggestion exists. */
  suggestedPricePerPiece?: number;
  /**
   * Unrounded price per piece based on buyer's suggested price — used ONLY
   * for total arithmetic; falls back to the (rounded)
   * suggestedPricePerPiece prop if not supplied, for backward compatibility
   * with any caller that doesn't pass a raw variant.
   */
  suggestedPricePerPieceRaw?: number;
}>) {
  const { t } = useTranslation(['order', 'cart']);

  const lineQuantity =
    unit === 'PCS' && totalPieces != null
      ? Math.round(totalPieces)
      : itemQuantity;
  let listedLineTotal =
    pricePerUnit != null ? pricePerUnit * lineQuantity : null;
  let suggestedLineTotal = (suggestedPrice ?? 0) * lineQuantity;
  if (pricePerPiece != null && totalPieces != null) {
    const pieces = Math.round(totalPieces);
    const listedPiecePrice = pricePerPieceRaw ?? pricePerPiece;
    const suggestedPiecePrice =
      suggestedPricePerPieceRaw ?? suggestedPricePerPiece;
    listedLineTotal = listedPiecePrice * pieces;
    suggestedLineTotal =
      (suggestedPiecePrice != null && suggestedPiecePrice > 0
        ? suggestedPiecePrice
        : listedPiecePrice) * pieces;
  }

  return (
    <div className="divide-y divide-neut-100 p-6 flex gap-4">
      <div className="w-20 h-20 bg-gradient-to-br from-warning-50 to-warning-100 rounded-lg flex items-center justify-center shrink-0">
        <div className="text-2xl">🌲</div>
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <h4 className="text-sm text-neut-prim font-medium mb-1">
          {productName}
        </h4>
        <p className="text-xs text-neut-sec">
          {gradeLabel}: {gradeInformation}{' '}
          <span className="px-2 py-1 bg-neut-100 rounded-md">{length}</span>
        </p>
        {buyCode != null && buyCode !== '' && (
          <p className="text-xs text-neut-sec" data-testid="order-item-buycode">
            {t('order.buy_code')}: {buyCode}
          </p>
        )}
        <p className="flex items-center gap-1 text-xs text-neut-sec">
          {unit === 'PCS' && totalPieces != null
            ? Math.round(totalPieces)
            : itemQuantity}{' '}
          {unit} ×{' '}
          {pricePerUnit != null
            ? `${formatCurrency(pricePerUnit, currency)}/${priceUnitLabel(unit)}`
            : t('order.not_available')}
          {pricePerPiece != null && unit === 'PCS' && (
            <PricePerPieceTooltip
              pricePerPiece={pricePerPiece}
              ns="cart"
              iconOnly
              className="text-neut-sec"
            />
          )}
        </p>
        {pricePerPiece != null && unit !== 'PCS' && (
          <PricePerPieceTooltip
            pricePerPiece={pricePerPiece}
            currency={currency}
            ns="cart"
            className="text-xs font-semibold text-neut-sec"
          />
        )}
        {vehiclePayload != null &&
          vehiclePayload.value > 0 &&
          vehicleLabel != null && (
            <VehiclePayloadLabel
              quantity={vehiclePayload}
              vehicleLabel={vehicleLabel}
              piecesCount={vehiclePiecesCount}
            />
          )}
        {piecesPerBundle != null && piecesPerBundle.value > 0 && (
          <p className="text-xs text-neut-sec mt-1 flex items-center gap-1">
            <Package size={12} aria-hidden="true" />
            {piecesPerBundle.value} {piecesPerBundle.unit}/
            {t('order.bundles_one')}
            {totalPieces != null && unit !== 'PCS' && (
              <strong className="text-neut-sec">
                ({Math.round(totalPieces)} PCS)
              </strong>
            )}
          </p>
        )}
        {suggestedPrice != null &&
          suggestedPrice > 0 &&
          (buyerView ? (
            <div className="mt-2 p-2 bg-warning-50 border border-warning-200 rounded-lg">
              <p className="text-xs text-warning-700">
                {t('order.your_offer')}{' '}
                {formatCurrency(suggestedPrice, currency)}/
                {priceUnitLabel(unit)}
              </p>
              {suggestedPricePerPiece != null && (
                <PricePerPieceTooltip
                  pricePerPiece={suggestedPricePerPiece}
                  currency={currency}
                  ns="cart"
                  className="text-xs font-semibold text-warning-700 mt-0.5"
                />
              )}
            </div>
          ) : (
            <div className="mt-2 p-2 bg-warning-50 border border-warning-200 rounded-lg">
              <p className="text-xs text-warning-700 flex items-center gap-1">
                <Tag size={12} aria-hidden="true" />
                {t('order.buyer_suggests')}{' '}
                {formatCurrency(suggestedPrice, currency)}/
                {priceUnitLabel(unit)}
              </p>
              {suggestedPricePerPiece != null && (
                <PricePerPieceTooltip
                  pricePerPiece={suggestedPricePerPiece}
                  currency={currency}
                  ns="cart"
                  className="text-xs font-semibold text-warning-700 mt-0.5"
                />
              )}
              {pricePerUnit != null && (
                <p className="text-xs text-neut-500">
                  {t('order.suggested_price_difference', {
                    amount: formatCurrency(
                      Math.abs(pricePerUnit - suggestedPrice),
                      currency
                    ),
                    direction: t(
                      suggestedPrice < pricePerUnit
                        ? 'order.suggested_price_less'
                        : 'order.suggested_price_more'
                    ),
                  })}
                </p>
              )}
            </div>
          ))}
      </div>
      <div className="text-right shrink-0">
        <p
          className="text-sm text-neut-prim font-semibold"
          data-testid="item-line-total"
        >
          {listedLineTotal != null
            ? formatCurrency(roundToCents(listedLineTotal), currency)
            : t('order.not_available')}
        </p>
        {suggestedPrice != null && suggestedPrice > 0 && (
          <p
            className="text-sm text-warning-700"
            data-testid="item-suggested-line-total"
          >
            {formatCurrency(roundToCents(suggestedLineTotal), currency)}
          </p>
        )}
      </div>
    </div>
  );
}
